import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk'
import { parseApiAsset } from '@morpho-org/dapp-core'

import type { VaultTransaction } from '@/lib/procedures/vaultTransactions.contract'

import type { VaultTransactionsTableRow, VaultTransactionType } from './config'
import type { VaultTransactionsCursorPage } from './vaultTransactionsQuery'

interface ToVaultTransactionsTableRowsParams {
  page: VaultTransactionsCursorPage | undefined
  chainId: ChainId
  /** Maps the transaction `type` to the displayed label (V1 uses "Vault …" labels). */
  typeLabels: Record<VaultTransactionType, string>
  /**
   * Which actor the User column reflects on a deposit or withdrawal: `sender`
   * (default) or `onBehalf`. A transfer always shows its sender.
   */
  userAddressMode?: 'sender' | 'onBehalf'
}

function userAddressOf(tx: VaultTransaction, mode: 'sender' | 'onBehalf') {
  switch (tx.type) {
    case 'deposit':
    case 'withdraw':
      return mode === 'onBehalf' ? tx.onBehalf : tx.sender
    case 'transfer':
      return tx.from
  }
}

/**
 * Shared row-mapping helper for every vault transaction table. Turns one
 * `vault.transactions` page into `VaultTransactionsTableRow`s, with the
 * page's asset on every row.
 */
export const toVaultTransactionsTableRows = ({
  page,
  chainId,
  typeLabels,
  userAddressMode = 'sender'
}: ToVaultTransactionsTableRowsParams): VaultTransactionsTableRow[] => {
  if (!page) return []

  const asset = page.asset
  const vaultToken =
    asset &&
    parseApiAsset({
      address: asset.address,
      symbol: asset.symbol ?? '',
      name: asset.name ?? asset.symbol ?? '',
      decimals: asset.decimals,
      logoURI: asset.logoUri,
      chain: { id: chainId },
      priceUsd: asset.priceUsd,
      yield: asset.yieldApy
    })

  return page.items.map(tx => ({
    chainId,
    hash: tx.txHash,
    timestamp: BigInt(tx.timestamp),
    type: tx.type,
    typeText: typeLabels[tx.type],
    urlHash: ChainUtils.getExplorerTransactionUrl(chainId, tx.txHash),
    user: { address: userAddressOf(tx, userAddressMode) },
    vaultToken,
    assets: tx.assets === null ? undefined : BigInt(tx.assets),
    assetsUsd: tx.assetsUsd
  }))
}
